import { useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Plus,
  LayoutGrid,
  List,
  Monitor,
  Building2,
  Users,
  Globe,
  LayoutList,
  Search,
  Download,
  Upload,
  Settings,
} from 'lucide-react';
import { useSpaceByKey } from '@/hooks/queries/useSpaces';
import { useObjects } from '@/hooks/queries/useObjects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ObjectFormModal } from '@/components/objects/ObjectFormModal';
import type { ObjectType, ObjectStatus, ObjectResponse } from '@arkitekt/shared';

const objectTypeIcons: Record<ObjectType, React.ReactNode> = {
  Application: <Monitor className="h-5 w-5" />,
  Technology: <LayoutGrid className="h-5 w-5" />,
  BusinessCapability: <Building2 className="h-5 w-5" />,
  Person: <Users className="h-5 w-5" />,
  Organization: <Globe className="h-5 w-5" />,
};

const objectTypeLabels: Record<ObjectType, string> = {
  Application: 'Applikasjon',
  Technology: 'Teknologi',
  BusinessCapability: 'Forretningskapabilitet',
  Person: 'Person',
  Organization: 'Organisasjon',
};

const statusVariants: Record<ObjectStatus, 'default' | 'success' | 'warning' | 'secondary'> = {
  draft: 'secondary',
  active: 'success',
  deprecated: 'warning',
  retired: 'secondary',
};

const statusLabels: Record<ObjectStatus, string> = {
  draft: 'Utkast',
  active: 'Aktiv',
  deprecated: 'Utfaset',
  retired: 'Pensjonert',
};

type ViewMode = 'grid' | 'list';

export function SpaceDetailPage() {
  const params = useParams<{ spaceKey: string }>();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ObjectType | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: space, isLoading: spaceLoading } = useSpaceByKey(params.spaceKey);
  const { data: objectsData, isLoading: objectsLoading } = useObjects(space?.id, {
    type: typeFilter ?? undefined,
    limit: 50,
  });

  const objects = objectsData?.data || [];

  // Filter by search query
  const filteredObjects = objects.filter(
    (obj) =>
      obj.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obj.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Export to Excel
  const handleExport = useCallback(() => {
    if (objects.length === 0) return;

    // Transform objects to a flat structure for Excel
    const exportData = objects.map((obj) => ({
      ID: obj.id,
      Type: obj.type,
      Navn: obj.name,
      Beskrivelse: obj.description || '',
      Status: obj.status,
      Opprettet: new Date(obj.createdAt).toLocaleDateString('nb-NO'),
      Oppdatert: new Date(obj.updatedAt).toLocaleDateString('nb-NO'),
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Objekter');

    // Generate filename with space key and date
    const date = new Date().toISOString().slice(0, 10);
    const filename = `${params.spaceKey}-objekter-${date}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  }, [objects, params.spaceKey]);

  if (spaceLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
        <p>Arbeidsområdet ble ikke funnet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{space.iconEmoji || '📁'}</span>
          <div>
            <h1 className="text-2xl font-bold">{space.name}</h1>
            {space.description && <p className="text-muted-foreground mt-1">{space.description}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/spaces/${params.spaceKey}/settings`}>
            <Button variant="outline">
              <Settings className="mr-2 h-4 w-4" />
              Innstillinger
            </Button>
          </Link>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Eksporter
          </Button>
          <Link to={`/spaces/${params.spaceKey}/import`}>
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Importer
            </Button>
          </Link>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nytt objekt
          </Button>
        </div>
      </div>

      {/* Create Object Modal */}
      {space && (
        <ObjectFormModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          spaceId={space.id}
          onSuccess={(newObject) => {
            navigate(`/spaces/${params.spaceKey}/objects/${newObject.id}`);
          }}
        />
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Søk i dette arbeidsområdet..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Type:</span>
          <div className="flex gap-1">
            <Button
              variant={typeFilter === null ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setTypeFilter(null)}
            >
              Alle
            </Button>
            {(['Application', 'Technology', 'BusinessCapability'] as ObjectType[]).map((type) => (
              <Button
                key={type}
                variant={typeFilter === type ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setTypeFilter(type)}
              >
                {objectTypeIcons[type]}
              </Button>
            ))}
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 border rounded-md p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {objectsLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredObjects.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <LayoutList className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">
              {searchQuery || typeFilter ? 'Ingen treff' : 'Ingen objekter ennå'}
            </h3>
            <p className="mt-2 text-muted-foreground">
              {searchQuery || typeFilter
                ? 'Prøv å endre søket eller filteret'
                : 'Opprett ditt første arkitekturobjekt'}
            </p>
            {!searchQuery && !typeFilter && (
              <Button className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Nytt objekt
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredObjects.map((obj) => (
            <ObjectCard key={obj.id} object={obj} spaceKey={params.spaceKey!} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredObjects.map((obj) => (
            <ObjectListItem key={obj.id} object={obj} spaceKey={params.spaceKey!} />
          ))}
        </div>
      )}

      {/* Pagination info */}
      {objectsData?.pagination && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Viser {filteredObjects.length} av {objectsData.pagination.total} objekter
        </div>
      )}
    </div>
  );
}

function ObjectCard({ object, spaceKey }: { object: ObjectResponse; spaceKey: string }) {
  return (
    <Link to={`/spaces/${spaceKey}/objects/${object.id}`}>
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              {objectTypeIcons[object.type]}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate">{object.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {objectTypeLabels[object.type]}
                </Badge>
                <Badge variant={statusVariants[object.status]} className="text-xs">
                  {statusLabels[object.status]}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="line-clamp-2">
            {object.description || 'Ingen beskrivelse'}
          </CardDescription>
          {object.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {object.tags.slice(0, 3).map((tag) => (
                <Badge key={tag.id} variant="secondary" className="text-xs">
                  {tag.name}
                </Badge>
              ))}
              {object.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{object.tags.length - 3}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function ObjectListItem({ object, spaceKey }: { object: ObjectResponse; spaceKey: string }) {
  return (
    <Link to={`/spaces/${spaceKey}/objects/${object.id}`}>
      <div className="flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-accent">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
          {objectTypeIcons[object.type]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate">{object.name}</h3>
            <Badge variant="outline" className="text-xs shrink-0">
              {objectTypeLabels[object.type]}
            </Badge>
            <Badge variant={statusVariants[object.status]} className="text-xs shrink-0">
              {statusLabels[object.status]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground truncate">{object.description || 'Ingen beskrivelse'}</p>
        </div>
        <div className="text-sm text-muted-foreground shrink-0">
          {new Date(object.updatedAt).toLocaleDateString('nb-NO')}
        </div>
      </div>
    </Link>
  );
}
